import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type AgentSessionResponse } from '@air/server-sdk';

import { WORKSPACE_AGENT_MY_TASKS_FETCH_NEXT_PAGE_ROW_THRESHOLD } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMyTasksRowLayout';
import {
  buildWorkspaceAgentSessionVirtualRows,
  estimateWorkspaceAgentSessionVirtualRowSize,
  type WorkspaceAgentOnboardingReviewTaskPlacement,
  type WorkspaceAgentSessionVirtualRow,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionVirtualRows';
import { focusWorkspaceAgentMyTasksAutomationRow } from '~/components/v2/components/workspace/workspace-agent-panel/utils/focusWorkspaceAgentMyTasksAutomationRow';
import { type Automation } from '~/hooks/queries/automations/usePrivateAutomationsList';

export type UseWorkspaceAgentMyTasksVirtualListParams = {
  focusAutomationRowId?: string | null;
  onFocusAutomationRowComplete?: () => void;
  automations: Automation[];
  sessions: AgentSessionResponse[];
  fetchNextAutomationsPage: () => Promise<unknown>;
  fetchNextArchivedPage: () => Promise<unknown>;
  fetchNextLivePage: () => Promise<unknown>;
  hasNextAutomationsPage: boolean;
  hasNextArchivedPage: boolean;
  hasNextLivePage: boolean;
  isFetchingNextAutomationsPage: boolean;
  isFetchingNextArchivedPage: boolean;
  isFetchingNextLivePage: boolean;
  isAutomationsSectionExpanded: boolean;
  onboardingReviewTaskPlacement: WorkspaceAgentOnboardingReviewTaskPlacement | undefined;
  onAutomationsSectionExpandedChange: (isExpanded: boolean) => void;
  showAutomationsSection: boolean;
};

const getVisibleLoadMoreRow = (
  virtualRows: WorkspaceAgentSessionVirtualRow[],
  lastVisibleRowIndex: number,
): Extract<WorkspaceAgentSessionVirtualRow, { type: 'load-more' }> | undefined => {
  const startIndex = Math.max(0, lastVisibleRowIndex - WORKSPACE_AGENT_MY_TASKS_FETCH_NEXT_PAGE_ROW_THRESHOLD + 1);

  for (let index = lastVisibleRowIndex; index >= startIndex; index -= 1) {
    const row = virtualRows[index];

    if (row?.type === 'load-more') {
      return row;
    }
  }

  return undefined;
};

export const useWorkspaceAgentMyTasksVirtualList = ({
  automations,
  focusAutomationRowId,
  fetchNextAutomationsPage,
  onFocusAutomationRowComplete,
  fetchNextArchivedPage,
  fetchNextLivePage,
  hasNextAutomationsPage,
  hasNextArchivedPage,
  hasNextLivePage,
  isFetchingNextAutomationsPage,
  isFetchingNextArchivedPage,
  isFetchingNextLivePage,
  sessions,
  isAutomationsSectionExpanded,
  onboardingReviewTaskPlacement,
  onAutomationsSectionExpandedChange,
  showAutomationsSection,
}: UseWorkspaceAgentMyTasksVirtualListParams) => {
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const [isArchivedSectionExpanded, setIsArchivedSectionExpanded] = useState(false);

  const toggleArchivedSection = useCallback(() => {
    setIsArchivedSectionExpanded((previous) => !previous);
  }, []);

  const toggleAutomationsSection = useCallback(() => {
    onAutomationsSectionExpandedChange(!isAutomationsSectionExpanded);
  }, [isAutomationsSectionExpanded, onAutomationsSectionExpandedChange]);

  const virtualRows = useMemo(
    () =>
      buildWorkspaceAgentSessionVirtualRows({
        automations,
        hasNextAutomationsPage: isAutomationsSectionExpanded && hasNextAutomationsPage,
        hasNextArchivedPage: isArchivedSectionExpanded && hasNextArchivedPage,
        hasNextLivePage,
        sessions,
        isArchivedSectionExpanded,
        isAutomationsSectionExpanded,
        onboardingReviewTaskPlacement,
        showAutomationsSection,
      }),
    [
      automations,
      hasNextAutomationsPage,
      hasNextArchivedPage,
      hasNextLivePage,
      isArchivedSectionExpanded,
      isAutomationsSectionExpanded,
      onboardingReviewTaskPlacement,
      sessions,
      showAutomationsSection,
    ],
  );

  const virtualizer = useVirtualizer({
    count: virtualRows.length,
    estimateSize: (index) => {
      const row = virtualRows[index];

      return row ? estimateWorkspaceAgentSessionVirtualRowSize(row, virtualRows[index + 1]) : 0;
    },
    getScrollElement: () => scrollElementRef.current,
    overscan: 10,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastVisibleRowIndex = virtualItems[virtualItems.length - 1]?.index;

  useEffect(() => {
    if (!focusAutomationRowId) {
      return;
    }

    const didFocus = focusWorkspaceAgentMyTasksAutomationRow({
      automationId: focusAutomationRowId,
      scrollElementRef,
      virtualItems,
      virtualRows,
      virtualizer,
    });

    if (didFocus) {
      onFocusAutomationRowComplete?.();
    }
  }, [focusAutomationRowId, onFocusAutomationRowComplete, virtualItems, virtualRows, virtualizer]);

  useEffect(() => {
    const tryFetchLoadMore = (scope: WorkspaceAgentSessionVirtualRow & { type: 'load-more' }) => {
      if (scope.scope === 'live' && hasNextLivePage && !isFetchingNextLivePage) {
        void fetchNextLivePage();
        return true;
      }

      if (scope.scope === 'automations' && hasNextAutomationsPage && !isFetchingNextAutomationsPage) {
        void fetchNextAutomationsPage();
        return true;
      }

      if (scope.scope === 'archived' && hasNextArchivedPage && !isFetchingNextArchivedPage) {
        void fetchNextArchivedPage();
        return true;
      }

      return false;
    };

    const loadMoreRowsInViewport = virtualItems
      .map((item) => virtualRows[item.index])
      .filter(
        (row): row is Extract<WorkspaceAgentSessionVirtualRow, { type: 'load-more' }> => row?.type === 'load-more',
      );

    for (const row of loadMoreRowsInViewport) {
      if (tryFetchLoadMore(row)) {
        return;
      }
    }

    if (lastVisibleRowIndex === undefined) {
      return;
    }

    const isNearEnd =
      lastVisibleRowIndex >= virtualRows.length - WORKSPACE_AGENT_MY_TASKS_FETCH_NEXT_PAGE_ROW_THRESHOLD;

    if (!isNearEnd) {
      return;
    }

    const visibleLoadMoreRow = getVisibleLoadMoreRow(virtualRows, lastVisibleRowIndex);

    if (visibleLoadMoreRow) {
      tryFetchLoadMore(visibleLoadMoreRow);
    }
  }, [
    fetchNextAutomationsPage,
    fetchNextArchivedPage,
    fetchNextLivePage,
    hasNextAutomationsPage,
    hasNextArchivedPage,
    hasNextLivePage,
    isFetchingNextAutomationsPage,
    isFetchingNextArchivedPage,
    isFetchingNextLivePage,
    lastVisibleRowIndex,
    virtualItems,
    virtualRows,
  ]);

  return {
    isAutomationsSectionExpanded,
    isArchivedSectionExpanded,
    scrollElementRef,
    toggleAutomationsSection,
    toggleArchivedSection,
    virtualRows,
    virtualizer,
    virtualItems,
  };
};
